type MetaTag = React.JSX.IntrinsicElements["meta"];

interface LinkTag {
  rel: string;
  href: string;
  type?: string;
  sizes?: string;
}

interface MetaConfig {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: "website" | "article" | "profile" | "product";
  ogUrl?: string;
  twitterCard?: "summary" | "summary_large_image";
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonical?: string;
  noindex?: boolean;
  keywords?: string[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
}

export function createMetaTags(config: MetaConfig): {
  meta: MetaTag[];
  links?: LinkTag[];
} {
  const meta: MetaTag[] = [
    { name: "description", content: config.description },
  ];

  // Add keywords if provided
  if (config.keywords?.length) {
    meta.push({ name: "keywords", content: config.keywords.join(", ") });
  }

  // Add author if provided
  if (config.author) {
    meta.push({ name: "author", content: config.author });
  }

  // Robots directive
  if (config.noindex) {
    meta.push({ name: "robots", content: "noindex, nofollow" });
  }

  // OpenGraph tags
  meta.push({ property: "og:title", content: config.ogTitle || config.title });
  meta.push({
    property: "og:description",
    content: config.ogDescription || config.description,
  });
  meta.push({ property: "og:type", content: config.ogType || "website" });

  if (config.ogUrl) {
    meta.push({ property: "og:url", content: config.ogUrl });
  }

  if (config.ogImage) {
    meta.push({ property: "og:image", content: config.ogImage });
    meta.push({
      property: "og:image:alt",
      content: config.ogTitle || config.title,
    });
  }

  // Article-specific OpenGraph tags
  if (config.ogType === "article") {
    if (config.author) {
      meta.push({ property: "article:author", content: config.author });
    }
    if (config.publishedTime) {
      meta.push({
        property: "article:published_time",
        content: config.publishedTime,
      });
    }
    if (config.modifiedTime) {
      meta.push({
        property: "article:modified_time",
        content: config.modifiedTime,
      });
    }
    if (config.section) {
      meta.push({ property: "article:section", content: config.section });
    }
    if (config.tags?.length) {
      config.tags.forEach((tag) => {
        meta.push({ property: "article:tag", content: tag });
      });
    }
  }

  // Twitter Card tags
  const twitterCard =
    config.twitterCard || (config.ogImage ? "summary_large_image" : "summary");
  meta.push({ name: "twitter:card", content: twitterCard });
  meta.push({
    name: "twitter:title",
    content: config.twitterTitle || config.ogTitle || config.title,
  });
  meta.push({
    name: "twitter:description",
    content:
      config.twitterDescription || config.ogDescription || config.description,
  });

  if (config.twitterImage || config.ogImage) {
    meta.push({
      name: "twitter:image",
      content: config.twitterImage || config.ogImage!,
    });
  }

  meta.push({ title: config.title });

  const result: { meta: MetaTag[]; links?: LinkTag[] } = {
    meta,
  };

  // Canonical link
  if (config.canonical) {
    result.links = [{ rel: "canonical", href: config.canonical }];
  }

  return result;
}

// Brand-specific meta tag presets
export const createBrandMeta = (
  config: Omit<MetaConfig, "title"> & { title: string },
) =>
  createMetaTags({
    ...config,
    title: config.title.includes("Dream Coach Match")
      ? config.title
      : `${config.title} | Dream Coach Match`,
  });

export const createCoachMeta = (
  coachName: string,
  config: Omit<MetaConfig, "title">,
) =>
  createMetaTags({
    ...config,
    title: `${coachName} | Dream Coach Match`,
    ogType: "profile",
  });

export const createArticleMeta = (
  title: string,
  config: Omit<MetaConfig, "title" | "ogType">,
) =>
  createMetaTags({
    ...config,
    title: `${title} | Dream Coach Match Blog`,
    ogType: "article",
  });

export const createNoIndexMeta = (title: string, description: string) =>
  createMetaTags({
    title,
    description,
    noindex: true,
  });

// Structured data helpers
export function createPersonStructuredData(person: {
  name: string;
  jobTitle?: string;
  image?: string;
  url?: string;
  sameAs?: string[];
  description?: string;
  address?: {
    addressLocality?: string;
    addressRegion?: string;
    addressCountry?: string;
  };
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: person.name,
    ...(person.jobTitle && { jobTitle: person.jobTitle }),
    ...(person.image && { image: person.image }),
    ...(person.url && { url: person.url }),
    ...(person.sameAs && { sameAs: person.sameAs }),
    ...(person.description && { description: person.description }),
    ...(person.address && {
      address: { "@type": "PostalAddress", ...person.address },
    }),
  };
}

export function createArticleStructuredData(article: {
  headline: string;
  description?: string;
  image?: string;
  author?: string;
  publishedDate?: string;
  modifiedDate?: string;
  url?: string;
}) {
  const articleUrl = article.url?.trim();
  const articleId = articleUrl ? `${articleUrl}#article` : undefined;

  return {
    "@context": "https://schema.org",
    "@type": ["Article", "BlogPosting"],
    ...(articleId ? { "@id": articleId } : {}),
    headline: article.headline,
    ...(article.description && { description: article.description }),
    ...(article.image && { image: article.image }),
    ...(article.author && {
      author: {
        "@type": "Person",
        name: article.author,
      },
    }),
    ...(article.publishedDate && { datePublished: article.publishedDate }),
    ...(article.modifiedDate && { dateModified: article.modifiedDate }),
    ...(articleUrl && { mainEntityOfPage: { "@type": "WebPage", "@id": articleUrl } }),
    ...(articleUrl && { url: articleUrl }),
    inLanguage: "en",
    publisher: {
      "@type": "Organization",
      name: "Dream Coach Match",
      url: "https://dreamcoachmatch.com",
      logo: {
        "@type": "ImageObject",
        url: "https://dreamcoachmatch.com/img/logo/dcm-horizontal-1.png",
      },
    },
  };
}

export function createProfilePageStructuredData(page: {
  url: string;
  name: string;
  description?: string;
  image?: string;
  jobTitle?: string;
  sameAs?: string[];
}) {
  const pageUrl = page.url.trim();
  const profileId = `${pageUrl}#profile`;
  const personId = `${pageUrl}#person`;
  const sanitizedSameAs = page.sameAs
    ?.map((value) => value.trim())
    .filter((value) => 0 < value.length);
  const uniqueSameAs = sanitizedSameAs
    ? [...new Set(sanitizedSameAs)]
    : undefined;

  const graph = [
    {
      "@type": "ProfilePage",
      "@id": profileId,
      url: pageUrl,
      name: page.name,
      ...(page.description && { description: page.description }),
      inLanguage: "en",
      mainEntity: { "@id": personId },
    },
    {
      "@type": "Person",
      "@id": personId,
      name: page.name,
      url: pageUrl,
      ...(page.jobTitle && { jobTitle: page.jobTitle }),
      ...(page.description && { description: page.description }),
      ...(page.image && { image: page.image }),
      ...(uniqueSameAs && 0 < uniqueSameAs.length
        ? { sameAs: uniqueSameAs }
        : {}),
    },
  ] satisfies Array<Record<string, unknown>>;

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}

export function createServiceStructuredData(service: {
  name: string;
  description: string;
  provider: string;
  url?: string;
  price?: string;
  image?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    description: service.description,
    provider: {
      "@type": "Person",
      name: service.provider,
    },
    ...(service.url && { url: service.url }),
    ...(service.price && {
      offers: {
        "@type": "Offer",
        price: service.price,
        priceCurrency: "USD",
      },
    }),
    ...(service.image && { image: service.image }),
  };
}

export function createLocalBusinessStructuredData(business: {
  name: string;
  description: string;
  address: {
    streetAddress?: string;
    addressLocality: string;
    addressRegion: string;
    addressCountry: string;
    postalCode?: string;
  };
  telephone?: string;
  url?: string;
  image?: string;
  priceRange?: string;
  serviceArea?: string[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": business.url,
    name: business.name,
    description: business.description,
    address: {
      "@type": "PostalAddress",
      ...business.address,
    },
    ...(business.telephone && { telephone: business.telephone }),
    ...(business.url && { url: business.url }),
    ...(business.image && { image: business.image }),
    ...(business.priceRange && { priceRange: business.priceRange }),
    ...(business.serviceArea && {
      areaServed: business.serviceArea.map((area) => ({
        "@type": "City",
        name: area,
      })),
    }),
  };
}
